import { UseMutationResult, useMutation } from 'react-query';

import { useAuth } from './useAuth';
import { getUserEmailNotification } from 'src/utils/urls';

import { UserEmailNotificationType } from 'src/types/emailNotification';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type TResult<TData = unknown> = {
  Errors: ApiErrorItem[];
  ErrorId: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: TData;
};

type TParamType = {
  id: number | string;
  payload: UserEmailNotificationType;
};

type TVariables = {
  token: string;
  tokenType: string;
  params: TParamType;
};

type TError = Error;

async function updateEmail({
  token,
  tokenType,
  params,
}: TVariables): Promise<TResult> {
  const response = await fetch(getUserEmailNotification(params?.id), {
    method: 'PUT',
    headers: {
      Authorization: `${tokenType} ${token}`,
      Tenant: process.env.REACT_APP_TENANT_ID || '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params?.payload),
  });

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useUpdateUserEmailNotificationSettings(): UseMutationResult<
  TResult,
  TError,
  TParamType,
  unknown
> {
  const { token, tokenType } = useAuth();

  return useMutation(async (params: TParamType) => {
    const result = await updateEmail({
      token,
      tokenType,
      params,
    });

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Failed to update email notification settings';

      throw new Error(errorMessage);
    }

    return result;
  });
}

export { useUpdateUserEmailNotificationSettings };
